import { Loading } from "../../../components";
import { useLocation, useNavigate } from "react-router-dom";
import { links } from "../../../constants/links";
import { useState, useEffect } from "react";
import {
  SUB_CAMPAIGN_ID_KEY, QR_SCANLOG_ID,
  COUPON_ID,
  LOGIN_METHOD,
  CHANNEL,
  CHANNEL_SESSION_ID
} from "../../../constants";
import {
  getCustomerSubcampaignStatus,
  postEngageAction,
  getSubCampaignRegister,
  getSubcampaignByChannel
} from "../../../services/ads/ads.service";

interface StateValue {
  merchant_reference: string;
  subcampaign_id: string;
  is_pay_get_coupon: boolean;
  phone_number: string;
  platform_type: string;
}

type Data = {
  is_engage_get_credit: boolean;
  advertiser_url: string;
  background: string;
  is_vending_machine?: boolean;
  is_pay_get_coupon?: boolean;
}

const VerifyQrCode = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const couponId = searchParams.get(COUPON_ID);
  const id = searchParams.get(SUB_CAMPAIGN_ID_KEY) || "";
  const qrScanlogID = searchParams.get(QR_SCANLOG_ID) || "";
  const channelId = searchParams.get(CHANNEL);
  const utmSource = searchParams.get("utm_source") ?? undefined;
  const utmMedium = searchParams.get("utm_medium") ?? undefined;
  const utmContent = searchParams.get("utm_content") ?? undefined;
  const utmCampaign = searchParams.get("utm_campaign") ?? undefined;

  const [isLoading, setIsLoading] = useState(!!(id || couponId || channelId));
  const [data, setData] = useState<Data>({
    is_engage_get_credit: false,
    advertiser_url: "",
    background: ""
  });

  const defaultApplication: string = process.env.REACT_APP_DEFAULT_APPLICATION || LOGIN_METHOD.LINE_PHONE_NUMBER;
  const {
    merchant_reference,
    subcampaign_id,
    is_pay_get_coupon
  }: StateValue = location.state || {};

  const [subcampaignId, setSubcampaignId] = useState<string>(id);

  useEffect(() => {
    if (!channelId && localStorage.getItem(CHANNEL_SESSION_ID)) {
      localStorage.removeItem(CHANNEL_SESSION_ID);
    }
  }, [channelId]);

  // Fetch subcampaign if channelId is present
  useEffect(() => {
    if (!channelId) return;

    let isMounted = true;

    getSubcampaignByChannel({
      channelId,
      qrSession: qrScanlogID,
      utmSource,
      utmMedium,
      utmContent,
      utmCampaign
    }).then((resData: any) => {
      if (!isMounted) return;

      const { subcampaign_id, channel_name, channel_session } = resData;

      localStorage.setItem(CHANNEL, channel_name);
      localStorage.setItem(SUB_CAMPAIGN_ID_KEY, subcampaign_id);
      localStorage.setItem(CHANNEL_SESSION_ID, channel_session);

      setSubcampaignId(subcampaign_id);
    }).catch((error: string) => {
      console.error("Error fetching subcampaign by channel", error);
      return navigate(`${links.CAMPAIGN_STATUS.replace(":status", "error")}`);
    });

    return () => {
      isMounted = false;
    };
  }, [channelId]);

  const handlePayGetCoupon = () => {
    localStorage.setItem(SUB_CAMPAIGN_ID_KEY, subcampaign_id);
    localStorage.setItem("merchant_reference", merchant_reference);
    localStorage.removeItem("session-id");
    navigate(links.LOGIN);
  };


  // Handle vending machine PayGetCoupon flow
  useEffect(() => {
    if (merchant_reference && subcampaign_id && is_pay_get_coupon) {
      handlePayGetCoupon();
    }
  }, [merchant_reference, subcampaign_id, is_pay_get_coupon]);

  const navigateToEngagePage = (id: string, additionalState = {}) => {
    navigate(links.ENGAGE_PAGE.replace(":id", id), { state: { ...additionalState } });
  };


  useEffect(() => {
    if (!subcampaignId && !couponId) return;
    setIsLoading(true);

    if (couponId) {
      localStorage.setItem(COUPON_ID, couponId);
      navigate(links.COUPON.replace(":id", couponId));
      return;
    } else {
      if (subcampaignId) {
        localStorage.setItem(SUB_CAMPAIGN_ID_KEY, subcampaignId);
        localStorage.setItem(QR_SCANLOG_ID, qrScanlogID);

        getSubCampaignRegister({ id: subcampaignId, qrSession: qrScanlogID }).then((resData: any) => {
          setData((prevData) => ({ ...prevData, ...resData }));
          const isEngageGetCredit: boolean = resData.is_engage_get_credit;
          const isSkipCampaignPage: boolean = Boolean(resData.is_skip_campaign_page);
          const subcampaignData = resData;
          const isNonVendingPayGetCoupon: boolean = resData.isNonVendingPayGetCoupon;

          if (localStorage.getItem("session-id")) {
            getCustomerSubcampaignStatus({ id: subcampaignId })
              .then(statusData => {
                if (!statusData?.status) {
                  return navigate(links.EARN_PAGE);
                }
                else {
                  const status: String = statusData.status;
                  const isPlatformOrSessionInvalid = ((statusData?.last_platform !== (statusData?.default_login_method || defaultApplication)) || statusData?.is_session_expired);
                  const forceLogin = statusData?.force_login || false;

                  if (forceLogin && !isEngageGetCredit && !isNonVendingPayGetCoupon) {
                    return navigate(links.LOGIN, { state: { forceLogin: true } });
                  }

                  if (isPlatformOrSessionInvalid) {
                    localStorage.removeItem("session-id");
                    if (isEngageGetCredit) {
                      return navigateToEngagePage(subcampaignId, {
                        isEngageGetCredit: true,
                        subcampaignData,
                        status,
                        isSkipCampaignPage,
                        channelSessionId: localStorage.getItem(CHANNEL_SESSION_ID) ?? ""
                      });
                    } else {
                      return navigate(links.LOGIN);
                    }
                  }
                  else {
                    postEngageAction({
                      subcampaign_id: parseInt(subcampaignId),
                      qrscanlog_id: localStorage.getItem("qrscanlog_id") ?? "",
                      channelSessionId: localStorage.getItem(CHANNEL_SESSION_ID) ?? "",
                      isVerifyPage: true
                    })
                      .then((res: any) => {
                        const engage_session_id = res.data?.engage_session_id;
                        if (engage_session_id !== undefined) {
                          localStorage.setItem("engage-session-id", engage_session_id);
                        }

                        if (isEngageGetCredit) {
                          return navigateToEngagePage(subcampaignId, {
                            isEngageGetCredit: true,
                            subcampaignData,
                            status,
                            isSkipCampaignPage,
                            channelSessionId: localStorage.getItem(CHANNEL_SESSION_ID) ?? ""
                          });
                        }

                        if (isNonVendingPayGetCoupon) {
                          return navigateToEngagePage(subcampaignId, {
                            isNonVendingPayGetCoupon: true,
                            subcampaignData,
                            status,
                            productName: resData.product_name,
                            productPrice: resData.product_price,
                            isProductPriceEditable: resData.is_product_price_editable,
                            qrscanlog_id: localStorage.getItem("qrscanlog_id") ?? "",
                            channelSessionId: localStorage.getItem(CHANNEL_SESSION_ID) ?? ""
                          });
                        }

                      }).catch((error) => {
                        console.log("Error during postEngageAction");
                      }).finally(() => {
                        if (!isEngageGetCredit && !isNonVendingPayGetCoupon) {
                          navigateToEngagePage(subcampaignId, {
                            status,
                            isSkipCampaignPage
                          });
                        }
                      });
                  }
                }
              }).catch((error) => {

                const msg = error?.response?.data?.error_msg;
                const advertiser_banner = error?.response?.data?.advertiser_banner;
                const banner_image_corner = error?.response?.data?.banner_image_corner;
                const engaged_subcampaign_id = error?.response?.data?.subcampaign_id;
                const last_engaged_session = error?.response?.data?.last_engaged_session;

                if (msg === "CAMPAIGN_ALREADY_EXIST") {
                  return navigate(links.ENGAGED_PAGE, {
                    state: {
                      subcampaign_id: engaged_subcampaign_id,
                      advertiser_banner: advertiser_banner,
                      banner_image_corner,
                      last_engaged_session: last_engaged_session
                    }
                  });
                }
              });
          } else {
            if (isEngageGetCredit) {
              return navigateToEngagePage(subcampaignId, {
                isEngageGetCredit: true,
                subcampaignData,
                isSkipCampaignPage,
                channelSessionId: localStorage.getItem(CHANNEL_SESSION_ID) ?? ""
              });
            }
            if (isNonVendingPayGetCoupon) {
              return navigateToEngagePage(subcampaignId, {
                isNonVendingPayGetCoupon: true,
                subcampaignData,
                productName: resData.product_name,
                productPrice: resData.product_price,
                qrscanlog_id: localStorage.getItem("qrscanlog_id") ?? "",
                channelSessionId: localStorage.getItem(CHANNEL_SESSION_ID) ?? "",
                isProductPriceEditable: resData.is_product_price_editable,
              });
            }

            else navigate(links.LOGIN, { state: { subcampaignData: resData } });
          }
        }).catch((error) => {
          let background = null;
          let error_code = null;
          let errorMsg = null;
          let banner_image_corner = "0";

          if (error?.response && error?.response?.data) {
            background = error.response.data?.background;
            error_code = error.response.data?.error_code;
            errorMsg = error.response.data?.error_msg;
            banner_image_corner = error.response.data?.banner_image_corner ?? "0";
          }

          if (errorMsg === "CAMPAIGN_ALREADY_EXIST") {

            const advertiser_banner = error.response.data?.advertiser_banner;
            const banner_image_corner = error.response.data?.banner_image_corner;
            const engaged_subcampaign_id = error.response.data?.subcampaign_id;
            const last_engaged_session = error.response.data?.last_engaged_session;

            return navigate(links.ENGAGED_PAGE, {
              state: {
                subcampaign_id: engaged_subcampaign_id,
                advertiser_banner: advertiser_banner,
                banner_image_corner,
                last_engaged_session: last_engaged_session
              }
            });
          }

          if (error_code === "engaged_limit_exceeded") {
            const limitEngageType = error.response.data?.limit_engage_type;
            return navigate(
              links.CAMPAIGN_LIMIT_ERROR,
              {
                state: {
                  advertiser_banner: background,
                  limit_engage_type: limitEngageType,
                  banner_image_corner,
                }
              }
            );
          }

          return navigate(
            `${links.CAMPAIGN_STATUS.replace(":status", "error")}`,
            { state: { background, banner_image_corner } }
          );
        });
      } else {
        navigate(links.EARN_PAGE);
      }
    }
  }, [subcampaignId, qrScanlogID]);

  return (
    <div>
      {isLoading && <Loading />}
    </div>
  );
};

export default VerifyQrCode;
